/**
 * App header. Renders only for authenticated users. Hidden on the public
 * Landing and Login routes (because `isAuthed` is false there).
 *
 * Role-specific nav:
 *   - admin   → Admin · Kitchen · Menu · History · Profile · Sign out (logo → /order)
 *   - teacher → Menu · History · Profile · Sign out                   (logo → /order)
 *   - barista → Sign out only                                          (logo → /kitchen)
 *
 * On phones (< sm) the links collapse behind a ☰ button on the LEFT that
 * opens a dropdown with every option; the inline row returns at sm and up.
 * Baristas have no links, so on mobile they just get the sign-out icon.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Menu as MenuIcon, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { useAuth } from '../hooks/useAuth';

interface NavItem {
  to: string;
  label: string;
  /** Admin link uses the gold accent. */
  accent?: boolean;
}

export function Header() {
  const { isAuthed, appUser, role, signOut } = useAuth();
  const navigate = useNavigate();

  if (!isAuthed) return null;

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/', { replace: true });
  };

  // Only the admin role surfaces a label — the only "tier" the UI advertises
  // is admin vs. everyone else.
  const isAdmin = role === 'admin';
  const isBarista = role === 'barista';
  const subtitle = isAdmin ? 'Admin account' : '';
  const logoHref = isBarista ? '/kitchen' : '/order';

  // Baristas don't see History/Profile/Admin/Menu — their only job is the
  // kitchen view and the shop-open toggle that lives there.
  const links: NavItem[] = [];
  if (isAdmin) {
    links.push(
      { to: '/admin', label: 'Admin', accent: true },
      { to: '/kitchen', label: 'Kitchen' },
    );
  }
  if (!isBarista) {
    links.push(
      { to: '/order', label: 'Menu' },
      { to: '/orders', label: 'History' },
      { to: '/profile', label: 'Profile' },
    );
  }

  return (
    <header
      className="bg-wca-surface border-b border-wca-gray-light px-4 sm:px-6 pb-3 flex items-center justify-between gap-3 sticky top-0 z-30"
      // Pad below the iOS status bar in standalone/home-screen mode (the app is
      // edge-to-edge via viewport-fit=cover). env() is 0 in normal browsers, so
      // this is a no-op there. Keeps the wordmark out from under the clock.
      style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {/* Mobile: ☰ on the left opens the full nav (below sm). */}
        {links.length > 0 && (
          <MobileMenu
            links={links}
            displayName={appUser?.displayName ?? ''}
            subtitle={subtitle}
            onSignOut={handleSignOut}
          />
        )}
        <Link
          to={logoHref}
          className="font-display text-xl text-wca-black hover:text-wca-red transition-colors whitespace-nowrap"
        >
          The Cracked Mug
        </Link>
      </div>

      {/* Desktop / tablet: inline nav (sm and up). */}
      <nav className="hidden sm:flex items-center gap-4">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={cn(
              'text-sm font-medium flex items-center transition-colors',
              link.accent
                ? 'font-semibold text-wca-gold hover:text-wca-red'
                : 'text-wca-gray-mid hover:text-wca-black',
            )}
          >
            {link.label}
          </Link>
        ))}
        {appUser !== null && (
          <div className="text-right">
            <p className="text-sm font-medium leading-tight">{appUser.displayName}</p>
            {subtitle.length > 0 && (
              <p className="text-xs text-wca-gold leading-tight font-medium">{subtitle}</p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="text-sm text-wca-red hover:text-wca-red-dark font-medium flex items-center"
        >
          Sign out
        </button>
      </nav>

      {/* Mobile barista: no nav links, so just the sign-out icon on the right. */}
      {links.length === 0 && (
        <button
          type="button"
          onClick={() => void handleSignOut()}
          aria-label="Sign out"
          className="sm:hidden text-wca-red hover:text-wca-red-dark min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <LogOut size={20} aria-hidden="true" />
        </button>
      )}
    </header>
  );
}

interface MobileMenuProps {
  links: NavItem[];
  displayName: string;
  subtitle: string;
  onSignOut: () => Promise<void>;
}

/** The phone-only ☰ button + dropdown. Closes on item select, outside click,
 *  or Escape. Owns its own open state so the Header stays presentational. */
function MobileMenu({ links, displayName, subtitle, onSignOut }: MobileMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex items-center sm:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-haspopup="menu"
        className="-ml-1 text-wca-black hover:text-wca-red min-h-[44px] min-w-[44px] flex items-center justify-center"
      >
        {open ? <X size={24} aria-hidden="true" /> : <MenuIcon size={24} aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-2 w-56 bg-wca-surface rounded-card shadow-xl border border-wca-gray-light overflow-hidden flex flex-col"
        >
          {displayName.length > 0 && (
            <div className="px-4 py-3 border-b border-wca-gray-light">
              <p className="text-sm font-medium leading-tight truncate">{displayName}</p>
              {subtitle.length > 0 && (
                <p className="text-xs text-wca-gold leading-tight font-medium">{subtitle}</p>
              )}
            </div>
          )}
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={cn(
                'px-4 py-3 text-sm font-medium min-h-[44px] flex items-center hover:bg-wca-gray-light transition-colors',
                link.accent ? 'text-wca-gold' : 'text-wca-black',
              )}
            >
              {link.label}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void onSignOut();
            }}
            className="px-4 py-3 text-sm font-medium min-h-[44px] flex items-center gap-2 text-wca-red hover:bg-wca-gray-light border-t border-wca-gray-light text-left transition-colors"
          >
            <LogOut size={16} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
